import {FC, RefObject, useState} from 'react';
import {Box, Stack} from '@chakra-ui/react';
import {IMemoryNote, useT} from '@kcast-fe/core';
import {HorizontalTabs, useScreenSize} from 'ui-kit';

import {TMemoryView} from '../../MemoryScene';

import {NoteEditorModal, NotesListTab, NotesSettingsTab} from './components';

type TNotesTab = 'list' | 'settings';

interface IProps {
  ref: RefObject<HTMLDivElement | null>;
  currentView: TMemoryView;
  onSetCurrentView: (view: TMemoryView) => void;
}

const NotesFC: FC<IProps> = ({ref, currentView, onSetCurrentView}) => {
  const [tab, setTab] = useState<TNotesTab>('list');
  const [editingNote, setEditingNote] = useState<IMemoryNote | null>(null);

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const isEditor = currentView === 'notes_create' || currentView === 'notes_edit';

  const openCreate = () => {
    setEditingNote(null);
    onSetCurrentView('notes_create');
  };

  const openEdit = (note: IMemoryNote) => {
    setEditingNote(note);
    onSetCurrentView('notes_edit');
  };

  const closeEditor = () => {
    setEditingNote(null);
    onSetCurrentView('notes_list');
  };

  if (isEditor) {
    return (
      <Box ref={ref} h={'calc(100% - 4px)'} overflow={'hidden'}>
        <NoteEditorModal note={editingNote} onClose={closeEditor} />
      </Box>
    );
  }

  const tabs = [
    {id: 'list', label: t('notes.tabs.list')},
    {id: 'settings', label: t('notes.tabs.settings')}
  ];

  return (
    <Box ref={ref} h={'calc(100% - 4px)'} overflow={'hidden'}>
      <Stack h={'100%'} gap={'0'}>
        <Box p={isLessThan768 ? '16px 12px 0 12px' : '12px 28px 0 28px'}>
          <HorizontalTabs tabs={tabs} selectedId={tab} onSelect={(id) => setTab(id as TNotesTab)} />
        </Box>

        {tab === 'list' && (
          <Box flex={'1 1 auto'} overflow={'hidden'}>
            <NotesListTab containerRef={ref} onOpenCreate={openCreate} onOpenEdit={openEdit} />
          </Box>
        )}
        {tab === 'settings' && (
          <Box
            flex={'1 1 auto'}
            overflow={'hidden'}
            p={isLessThan768 ? '24px 16px 12px 16px' : '16px 24px'}
          >
            <NotesSettingsTab onSaved={() => setTab('list')} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export const Notes = NotesFC;
